import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  type BusinessAddressFormValues,
  officeAddressSchema,
  type OfficeAddressFormValues,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Checkbox } from '@breezeventures/ui-kit/checkbox/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { AddressSuggestions } from '@breezeventures/web-ph-address-suggestions/address-suggestions';
import { zodResolver } from '@hookform/resolvers/zod';

import { businessAddressToOfficeAddress } from './lib';

const suggestionFieldNames = {
  country: 'country',
  region: 'region',
  province: 'province',
  cityOrMunicipality: 'cityOrMunicipality',
  postalCode: 'postalCode',
  barangay: 'barangay',
};

const emptyOfficeAddressFormValues: OfficeAddressFormValues = {
  sameAsRegisteredAddress: false,
  country: 'PHL',
  region: '',
  province: '',
  cityOrMunicipality: '',
  postalCode: '',
  barangay: '',
  streetAndHouse: '',
  unitAndFloor: '',
} as const;

const officeAddressFormFields = Object.keys(
  emptyOfficeAddressFormValues
) as Array<keyof typeof emptyOfficeAddressFormValues>;

type OfficeAddressProps = {
  initialValues?: OfficeAddressFormValues;
  businessAddress?: BusinessAddressFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: OfficeAddressFormValues) => Promise<void>;
};

export const OfficeAddress = ({
  initialValues,
  businessAddress,
  onBack,
  onClose,
  onSubmit,
}: OfficeAddressProps) => {
  const methods = useForm<OfficeAddressFormValues>({
    resolver: zodResolver(officeAddressSchema),
    mode: 'all',
    defaultValues: initialValues ?? emptyOfficeAddressFormValues,
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const isSameAsRegisteredAddress = methods.watch('sameAsRegisteredAddress');

  const handleSameAsRegisteredAddressChange = (value: boolean) => {
    if (value && businessAddress) {
      const valueFromOfficeAddress =
        businessAddressToOfficeAddress(businessAddress);
      methods.reset(valueFromOfficeAddress);
      return;
    }

    officeAddressFormFields.forEach((field) => {
      if (field === 'sameAsRegisteredAddress') {
        methods.setValue(field, false);
      } else {
        methods.setValue(field, '');
      }
    });
  };

  const handleSubmit = async (values: OfficeAddressFormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="py-6">
            What&apos;s the address of the main office?
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <Controller
              name="sameAsRegisteredAddress"
              control={methods.control}
              render={({ field, ...rest }) => (
                <Checkbox
                  {...rest}
                  field={{
                    ...field,
                    onChange: (value) => {
                      field.onChange(value);
                      handleSameAsRegisteredAddressChange(value);
                    },
                  }}
                >
                  <Typography.Text size="m" color="secondary">
                    Same as registered address
                  </Typography.Text>
                </Checkbox>
              )}
            />

            <AddressSuggestions
              fieldNames={suggestionFieldNames}
              disabled={isSameAsRegisteredAddress}
            />

            <Controller
              name="streetAndHouse"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="House number, Street name"
                  labelPosition="inner"
                  disabled={isSameAsRegisteredAddress}
                />
              )}
            />

            <Controller
              name="unitAndFloor"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Unit/Apartment (if any)"
                  labelPosition="inner"
                  disabled={isSameAsRegisteredAddress}
                />
              )}
            />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
